// ModernCommandCluster — ツールでないアクションの小クラスタ (§9.6 コマンドクラスタ).
//
// 方針 (docs/_wip/modern-layout.md §9.6):
//   - ツール (相互排他) でない **アクション** をツール帯とは別の小クラスタに置く:
//     全消し / キャプチャ / 画像貼付 / 名前ラベル表示。
//   - レイヤーボタンは右ドック常設化で不要 (§9.6)。プレビューも中右ドック最上部に
//     常設化したのでトグルボタンは廃止 (パネル配置調整 2026-06-08)。
//   - 全消しは Classic と同じく ConfirmDialog 経由 (新規プロトコルイベントなし) を
//     親 (ModernLayout) が担い、ここは onClear を呼ぶだけ。
//   - 画像貼付は permission gate (canImagepaste=false なら非表示)。
//   - タイムラプス共有 (T3、growth-timelapse-share-2026-07-02.md §5.3) は support
//     gate (onShareTimelapse=null = WebCodecs H.264 非対応環境では **非表示**)。
//     フォンシェルはオーバーフローシートが本クラスタを再ホストするので入口はここ 1 箇所。
//   - inline SVG アイコン (新 dep なし)。
//
// engine/protocol には触れない。各アクションは親から渡されたハンドラを呼ぶだけ。

import { useRef } from 'react';
import { useConnectionLocked } from '../../providers/ConnectionLockContext.js';

import {
  CaptureIcon,
  HistoryIcon,
  ImageIcon,
  NameTagIcon,
  TimelapseFilmIcon,
  TrashIcon,
  UndoIcon,
} from './ModernIcons.js';

export interface ModernCommandClusterProps {
  /** 全消し可否 (permission + clearOpen)。 */
  readonly canClear: boolean;
  readonly onClear: () => void;
  /**
   * conflict-aware Undo (undo-conflict-aware-2026-07-12.md)。
   * `useConflictAwareUndo.canUndo` / `.triggerUndo` をそのまま渡す。Undo 機能自体は
   * Classic/Modern 共通 (2026-07-23 是正、modern-layout.md §1.1 表現パリティ) だが、
   * このクラスタ (常設ボタン) 自体は Modern 限定 UI。Classic は ToolPanel を
   * 増やさず、ショートカット (Ctrl/Cmd+Z) + 拒否トースト/ハイライトを受け皿にする
   * (`ClassicLayout` に個別配線、常設ボタンは新設しない)。
   */
  readonly canUndo: boolean;
  readonly onUndo: () => void;
  /**
   * Undo ボタンに「New!」バッジを出すか (2026-07-23 追補、undo-conflict-aware-2026-07-12.md)。
   * `useConflictAwareUndo.showBadge` をそのまま渡す。初回にボタン/Ctrl+Z のどちらかで
   * Undo を実行すると (成功/拒否問わず) 親経由で false に切り替わる。Classic/Modern
   * 共有の 1 本の localStorage key なので、片方の入室で消すともう片方でも消える。既定 false。
   */
  readonly showUndoBadge?: boolean;
  /** キャプチャ (PNG ダウンロード)。 */
  readonly onCapture: () => void;
  /** 画像貼付 (permission gate: null なら非表示)。 */
  readonly onImagepasteFile: ((file: File) => void) | null;
  /** 名前ラベル表示トグル。 */
  readonly nameLabelMode: boolean;
  readonly onToggleNameLabelMode: () => void;
  /** タイムラプス共有 (support gate: null なら非表示 = WebCodecs 非対応環境 or flag off)。 */
  readonly onShareTimelapse: (() => void) | null;
  /**
   * 絵ログ共有ボタンを押せるのがオーナー本人か (2026-07-08 方針変更)。ボタンは全員に見せ、
   * false (= 非オーナー) のとき押すとオーナーログインへ誘導する導線扱いにし、tooltip / aria
   * をログイン誘導文言に切替える。既定 true (= 従来どおりの共有ボタン)。
   */
  readonly timelapseShareOwner?: boolean;
  /**
   * 絵ログ共有ボタンに「New!」バッジを出すか (consult 2026-07-08)。初回のみ true → 押すと
   * 親が false に切替える。onShareTimelapse が null (= ボタン非表示) のときは無視。既定 false。
   */
  readonly showTimelapseBadge?: boolean;
  /**
   * 過去キャンバス一覧を開く (Slice 2、past-canvas-new-document-2026-07-05.md §7)。
   * null なら非表示。ボタンは全員に見せ、非オーナーが押すと親が
   * オーナーログインへ誘導する (絵ログと同じ流儀)。
   */
  readonly onOpenPastCanvas?: (() => void) | null;
  /** 過去キャンバス入口を押せるのがオーナー本人か (tooltip/aria の出し分け)。既定 true。 */
  readonly pastCanvasOwner?: boolean;
  /** 過去キャンバス入口に「New!」バッジを出すか。初回のみ true。既定 false。 */
  readonly showPastCanvasBadge?: boolean;
}

const actionButtonClass = (active = false): string =>
  `flex h-9 w-9 items-center justify-center rounded-md border transition-colors ${
    active
      ? 'border-[var(--color-accent)] bg-[var(--color-accent)] text-black'
      : 'border-[var(--color-border)] bg-[var(--color-bg-soft)] text-white hover:bg-[var(--color-bg)]'
  } disabled:opacity-40`;

export const ModernCommandCluster = ({
  canClear,
  onClear,
  canUndo,
  onUndo,
  showUndoBadge = false,
  onCapture,
  onImagepasteFile,
  nameLabelMode,
  onToggleNameLabelMode,
  onShareTimelapse,
  timelapseShareOwner = true,
  showTimelapseBadge = false,
  onOpenPastCanvas = null,
  pastCanvasOwner = true,
  showPastCanvasBadge = false,
}: ModernCommandClusterProps): React.ReactNode => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // 再接続中は全消し / Undo が止まる: tooltip で接続待ちだと分かるようにする。
  const connectionLocked = useConnectionLocked();
  // 絵ログ共有ボタンの tooltip / aria: オーナーは「絵ログを共有」、非オーナーはログイン誘導。
  const shareTitle = timelapseShareOwner
    ? '絵ログを共有'
    : 'オーナーだけが絵ログを共有できます。オーナーはこちらからログイン';
  // 過去キャンバス入口の tooltip / aria: 同様にオーナー/非オーナーで出し分け。
  const pastCanvasTitle = pastCanvasOwner
    ? '全消しした過去のキャンバスを見る'
    : 'オーナーだけが過去キャンバスを見られます。オーナーはこちらからログイン';

  return (
    <div
      role="toolbar"
      aria-orientation="vertical"
      className="flex flex-col items-center gap-1.5 rounded-lg border border-[var(--color-border)] bg-[var(--color-panel)] p-1.5"
      aria-label="コマンド"
    >
      <button
        type="button"
        aria-label="全消し"
        title={connectionLocked ? '全消し — つながったら使えます' : '全消し'}
        data-manual="draw.clear"
        onClick={onClear}
        disabled={!canClear}
        className={actionButtonClass()}
      >
        <TrashIcon />
      </button>
      <button
        type="button"
        aria-label={showUndoBadge ? '元に戻す (Undo) (新機能)' : '元に戻す (Undo)'}
        title={
          connectionLocked
            ? '元に戻す (Undo) — つながったら使えます'
            : '元に戻す (Undo) — Ctrl+Z / Cmd+Z'
        }
        data-manual="draw.undo"
        onClick={onUndo}
        disabled={!canUndo}
        className={`relative ${actionButtonClass()}`}
      >
        <UndoIcon />
        {/* 「New!」バッジ (2026-07-23 追補): 絵ログ/過去キャンバス入口と同じ視覚言語
            (accent 塗り + pulse)。初回に Undo を実行すると親 (undo.showBadge) が false
            にして消える。意味は aria-label の「(新機能)」が担う (ピルは aria-hidden の装飾)。 */}
        {showUndoBadge ? (
          <span
            aria-hidden="true"
            className="-top-1.5 -right-1 absolute animate-pulse rounded-full border border-[var(--color-panel)] bg-[var(--color-accent)] px-1 py-px font-bold text-[8px] text-black leading-none shadow"
          >
            New!
          </span>
        ) : null}
      </button>
      <button
        type="button"
        aria-label="保存 (キャプチャ)"
        title="保存 (キャプチャ)"
        data-manual="keep.capture"
        onClick={onCapture}
        className={actionButtonClass()}
      >
        <CaptureIcon />
      </button>
      {onImagepasteFile !== null && (
        <>
          <button
            type="button"
            aria-label="画像を貼り付け"
            title={connectionLocked ? 'つながったら使えます' : '画像を貼り付け'}
            disabled={connectionLocked}
            data-manual="draw.imagepaste"
            onClick={() => fileInputRef.current?.click()}
            className={actionButtonClass()}
          >
            <ImageIcon />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const file = e.currentTarget.files?.[0];
              if (file) onImagepasteFile(file);
              e.currentTarget.value = '';
            }}
          />
        </>
      )}
      {onShareTimelapse !== null && (
        <button
          type="button"
          aria-label={showTimelapseBadge ? `${shareTitle} (新機能)` : shareTitle}
          title={shareTitle}
          data-manual="keep.timelapse"
          onClick={onShareTimelapse}
          className={`relative ${actionButtonClass()}`}
        >
          <TimelapseFilmIcon />
          {/* 🔒 ロックインジケータ (2026-07-08): 非オーナー (timelapseShareOwner=false)
              のときだけ、押すと owner ログインへ誘導される "壁" を **タップ前に** 控えめに
              示す。琥珀 (--color-accent) は使わず青系 (--color-bg-soft) に留め、New!
              (琥珀ピル) を埋もれさせない。New! は右上なので 🔒 は左下の別隅に置いて衝突回避。
              意味は aria-label / title (ログイン誘導文言) が既に担うので aria-hidden。
              owner 認証すると timelapseShareOwner が true になり反応的に消える。 */}
          {timelapseShareOwner === false ? (
            <span
              aria-hidden="true"
              className="-bottom-1 -left-1 absolute rounded-full border border-[var(--color-panel)] bg-[var(--color-bg-soft)] px-0.5 py-px text-[8px] text-white leading-none shadow"
            >
              🔒
            </span>
          ) : null}
          {/* 「New!」バッジ (consult 2026-07-08、2026-07-08 発見性強化): 初回のみ右上に
              accent 塗りの「New!」ピル + pulse 点滅で視線を引く。押すと親が
              showTimelapseBadge を false にして消える。縦レール内なので右への張り出しは
              旧ドットと同じ 4px に抑え、レイアウトは崩さない (absolute 配置)。意味は
              aria-label の「(新機能)」が担う (ピルは aria-hidden の装飾)。 */}
          {showTimelapseBadge ? (
            <span
              aria-hidden="true"
              className="-top-1.5 -right-1 absolute animate-pulse rounded-full border border-[var(--color-panel)] bg-[var(--color-accent)] px-1 py-px font-bold text-[8px] text-black leading-none shadow"
            >
              New!
            </span>
          ) : null}
        </button>
      )}
      {onOpenPastCanvas !== null && (
        <button
          type="button"
          aria-label={showPastCanvasBadge ? `${pastCanvasTitle} (新機能)` : pastCanvasTitle}
          title={pastCanvasTitle}
          data-manual="keep.past-canvas"
          onClick={onOpenPastCanvas}
          className={`relative ${actionButtonClass()}`}
        >
          <HistoryIcon />
          {/* 🔒 ロックインジケータ (2026-07-08)。絵ログ入口と同じ流儀: 非オーナー
              (pastCanvasOwner=false) のとき左下の別隅に控えめな青系 🔒 を出す (New! と
              衝突しない)。琥珀は使わない。意味は aria-label / title が担うので aria-hidden。 */}
          {pastCanvasOwner === false ? (
            <span
              aria-hidden="true"
              className="-bottom-1 -left-1 absolute rounded-full border border-[var(--color-panel)] bg-[var(--color-bg-soft)] px-0.5 py-px text-[8px] text-white leading-none shadow"
            >
              🔒
            </span>
          ) : null}
          {/* 「New!」バッジ (Slice 2)。絵ログ入口と同じ視覚言語 (accent 塗り + pulse)。
              押すと親が showPastCanvasBadge を false にして消える。意味は aria-label の
              「(新機能)」が担う (ピルは aria-hidden の装飾)。 */}
          {showPastCanvasBadge ? (
            <span
              aria-hidden="true"
              className="-top-1.5 -right-1 absolute animate-pulse rounded-full border border-[var(--color-panel)] bg-[var(--color-accent)] px-1 py-px font-bold text-[8px] text-black leading-none shadow"
            >
              New!
            </span>
          ) : null}
        </button>
      )}
      <button
        type="button"
        aria-label="名前ラベル表示"
        aria-pressed={nameLabelMode}
        title="名前ラベル表示"
        data-manual="together.name-label"
        onClick={onToggleNameLabelMode}
        className={actionButtonClass(nameLabelMode)}
      >
        <NameTagIcon />
      </button>
    </div>
  );
};
